import React, { useEffect, useRef, useState, useCallback } from "react"
import { twMerge } from "tailwind-merge"

// One player at a time, app-wide: starting playback pauses whichever
// AudioPlayer (or registered media) was playing before.
let currentlyPlaying = null
export function pauseOtherAudio(except) {
  if (currentlyPlaying && currentlyPlaying !== except) {
    try { currentlyPlaying.pause() } catch {}
  }
  currentlyPlaying = except || null
}

/**
 * AudioPlayer — compact custom audio player (no native chrome).
 *
 * Play/pause, seekable progress with buffered range, elapsed / total time,
 * optional playback-rate toggle. Works for meeting recordings, voice
 * messages in chat, any audio URL.
 *
 * <AudioPlayer src={url} title="…" />
 *
 * @param {string}  src
 * @param {string}  [title]          shown above the controls (optional)
 * @param {boolean} [autoPlay]
 * @param {boolean} [compact]        single-row layout for chat bubbles
 * @param {boolean} [showRate]       playback speed toggle (1× / 1.5× / 2×), default true
 * @param {number}  [initialDuration] known duration in seconds before metadata loads
 *                                   (MediaRecorder webm has none — pass the recorded length)
 * @param {number[]} [peaks]         0..1 amplitude buckets → waveform bars instead of a flat bar
 * @param {"light"|"dark"} [tone]    dark = for dark bubbles (in-call chat); default light (theme tokens)
 * @param {boolean} [inlineTime]     single-row layout: time to the right of the bar (Slack-style)
 * @param {React.ReactNode} [actions] extra controls rendered at the end (e.g. a ⋮ menu)
 * @param {Function}[onLoadedMetadata] (durationSeconds)
 * @param {Function}[onEnded]
 * @param {string}  [className]
 */
export default function AudioPlayer({
  src,
  title,
  autoPlay = false,
  compact = false,
  showRate = true,
  initialDuration = null,
  peaks = null,
  tone = "light",
  inlineTime = false,
  actions = null,
  onLoadedMetadata,
  onEnded,
  className = "",
}) {
  const audioRef = useRef(null)
  const barRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(initialDuration || 0)
  const [buffered, setBuffered] = useState(0)
  const [rate, setRate] = useState(1)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(false)
  const draggingRef = useRef(false)

  useEffect(() => {
    setPlaying(false); setCurrent(0); setBuffered(0); setReady(false); setError(false)
    setDuration(initialDuration || 0)
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (currentlyPlaying === audioRef.current) currentlyPlaying = null }, [])

  const toggle = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    if (a.paused) a.play().catch(() => {})
    else a.pause()
  }, [])

  const seekTo = useCallback((clientX) => {
    const a = audioRef.current, bar = barRef.current
    if (!a || !bar || !duration) return
    const r = bar.getBoundingClientRect()
    const p = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    a.currentTime = p * duration
    setCurrent(p * duration)
  }, [duration])

  const onPointerDown = (e) => {
    draggingRef.current = true
    e.currentTarget.setPointerCapture?.(e.pointerId)
    seekTo(e.clientX)
  }
  const onPointerMove = (e) => { if (draggingRef.current) seekTo(e.clientX) }
  const onPointerUp = () => { draggingRef.current = false }

  const onKeyDown = (e) => {
    const a = audioRef.current
    if (!a) return
    if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle() }
    if (e.key === "ArrowRight") a.currentTime = Math.min(duration, a.currentTime + 5)
    if (e.key === "ArrowLeft") a.currentTime = Math.max(0, a.currentTime - 5)
  }

  const cycleRate = () => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1
    setRate(next)
    if (audioRef.current) audioRef.current.playbackRate = next
  }

  const pct = duration ? Math.min(100, (current / duration) * 100) : 0
  const bufPct = duration ? Math.min(100, (buffered / duration) * 100) : 0
  const dark = tone === "dark"
  const T = dark
    ? { surface: "bg-white/10 border-white/15 text-white", muted: "text-white/70", track: "bg-white/25", buf: "bg-white/30", knobRing: "ring-black/40", rateBtn: "text-white/70 hover:text-white hover:bg-white/10", barOff: "bg-white/30" }
    : { surface: "bg-neutral border-bordered text-text", muted: "text-text_secondary", track: "bg-neutral_2", buf: "bg-neutral_2 brightness-95", knobRing: "ring-paper", rateBtn: "text-text_secondary hover:text-text hover:bg-black/5", barOff: "bg-neutral_2" }
  const hasPeaks = Array.isArray(peaks) && peaks.length > 1

  return (
    <div
      className={twMerge(
        "w-full rounded-2xl border select-none",
        T.surface,
        compact ? "px-3 py-2" : "px-4 py-3",
        className,
      )}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        autoPlay={autoPlay}
        onPlay={(e) => { pauseOtherAudio(e.currentTarget); setPlaying(true) }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => { if (!draggingRef.current) setCurrent(e.currentTarget.currentTime) }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration
          if (Number.isFinite(d) && d > 0) { setDuration(d); onLoadedMetadata?.(d) }
          setReady(true)
        }}
        onDurationChange={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d) && d > 0) setDuration(d) }}
        onEnded={() => { setPlaying(false); onEnded?.() }}
        onProgress={(e) => {
          const b = e.currentTarget.buffered
          if (b && b.length) setBuffered(b.end(b.length - 1))
        }}
        onError={() => setError(true)}
      />

      {title && !compact && (
        <p className="text-sm font-medium truncate m-0 mb-2">{title}</p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          disabled={error}
          aria-label={playing ? "Pause" : "Play"}
          className={twMerge(
            "flex-shrink-0 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary_dk transition-colors disabled:opacity-40",
            compact ? "w-8 h-8" : "w-10 h-10",
          )}
        >
          {playing ? (
            <svg className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h4v16H6zM14 4h4v16h-4z" /></svg>
          ) : (
            <svg className={compact ? "w-3.5 h-3.5 translate-x-px" : "w-4 h-4 translate-x-px"} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div
            ref={barRef}
            role="slider"
            tabIndex={0}
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(current)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onKeyDown}
            className={twMerge(
              "relative cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/40 touch-none",
              hasPeaks ? (compact ? "h-6" : "h-8") : `h-2 rounded-full ${T.track}`,
            )}
          >
            {hasPeaks ? (
              // Waveform: bars up to the playhead take the accent color.
              <div className="absolute inset-0 flex items-center gap-[2px]">
                {peaks.map((v, i) => {
                  const played = (i + 0.5) / peaks.length <= pct / 100
                  const h = Math.max(0.15, Math.min(1, v || 0))
                  return (
                    <span
                      key={i}
                      className={twMerge("flex-1 rounded-full transition-colors", played ? "bg-primary" : T.barOff)}
                      style={{ height: `${Math.round(h * 100)}%` }}
                    />
                  )
                })}
              </div>
            ) : (
              <>
                <div className={twMerge("absolute inset-y-0 left-0 rounded-full", T.buf)} style={{ width: `${bufPct}%` }} />
                <div className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${pct}%` }} />
                <div
                  className={twMerge("absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-primary shadow ring-2", T.knobRing)}
                  style={{ left: `${pct}%` }}
                />
              </>
            )}
          </div>
          {!inlineTime && (
            <div className={twMerge("mt-1 flex items-center justify-between text-[11px] tabular-nums", T.muted)}>
              <span>{fmt(current)}</span>
              <span>{error ? "—" : ready || duration ? fmt(duration) : "…"}</span>
            </div>
          )}
        </div>

        {inlineTime && (
          <span className={twMerge("flex-shrink-0 text-sm tabular-nums min-w-[2.75rem] text-right", T.muted)}>
            {error ? "—" : playing || current > 0 ? fmt(current) : (ready || duration ? fmt(duration) : "…")}
          </span>
        )}

        {showRate && (
          <button
            type="button"
            onClick={cycleRate}
            className={twMerge("flex-shrink-0 h-7 min-w-[2.5rem] px-2 rounded-full text-xs font-semibold tabular-nums", T.rateBtn)}
            aria-label="Playback speed"
          >
            {rate}×
          </button>
        )}

        {actions}
      </div>
    </div>
  )
}

function fmt(sec) {
  const t = Math.max(0, Math.floor(sec || 0))
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60
  const mm = String(m).padStart(2, "0"), ss = String(s).padStart(2, "0")
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}
